import {
  CREDIT_ECONOMICS,
  creditsFromKieCredits,
  seedance25GenerationCredits,
} from '@/config/credit-economics';
import {
  REFERENCE_TO_VIDEO_AUDIO_MAX,
  REFERENCE_TO_VIDEO_EXTRA_IMAGE_KIE_CREDITS,
  REFERENCE_TO_VIDEO_FREE_IMAGE_COUNT,
  REFERENCE_TO_VIDEO_IMAGE_MAX,
  REFERENCE_TO_VIDEO_MAX_FILES,
  REFERENCE_TO_VIDEO_VIDEO_MAX,
} from './reference-to-video';

export enum VideoGenerationModel {
  TOPAZ_VIDEO_UPSCALE = 'topaz-video-upscale',
  GROK_IMAGINE = 'grok-imagine',
  GROK_IMAGINE_1_5 = 'grok-imagine-1.5',
  GROK_IMAGINE_VIDEO_EDIT = 'grok-imagine-video-edit',
  KLING_2_1_MASTER = 'kling-2.1-master',
  KLING_2_5_TURBO = 'kling-2.5-turbo',
  KLING_2_6 = 'kling-2.6',
  KLING_3 = 'kling-3',
  KLING_3_TURBO = 'kling-3-turbo',
  SEEDANCE_1_5_PRO = 'seedance-1.5-pro',
  SEEDANCE_2 = 'seedance-2',
  SEEDANCE_2_FAST = 'seedance-2-fast',
  SEEDANCE_2_MINI = 'seedance-2-mini',
  /**
   * Defined for pricing only. The provider route is not verified, so this
   * model is excluded from workbench lists.
   */
  SEEDANCE_2_5 = 'seedance-2.5',
  BYTEDANCE_V1_PRO = 'bytedance-v1-pro',
  BYTEDANCE_V1_LITE = 'bytedance-v1-lite',
  WAN_2_2_A14B = 'wan-2.2-a14b',
  WAN_2_5 = 'wan-2.5',
  WAN_2_6 = 'wan-2.6',
  WAN_2_7 = 'wan-2.7',
  WAN_2_7_VIDEO_EDIT = 'wan-2.7-video-edit',
  HAILUO_02_PRO = 'hailuo-02-pro',
  HAILUO_02_STANDARD = 'hailuo-02-standard',
  MINIMAX_H3 = 'minimax-h3',
  HAPPYHORSE_1 = 'happyhorse-1',
  HAPPYHORSE_1_1 = 'happyhorse-1.1',
  GEMINI_OMNI = 'gemini-omni',
  RUNWAY_VIDEO = 'runway-video',
  VEO_3_1 = 'veo-3.1',
  VEO_3_1_LITE = 'veo-3.1-lite',
  VEO_3_1_QUALITY = 'veo-3.1-quality',
  BYTEDANCE_V1_LITE_I2V = 'bytedance-v1-lite-i2v',
  BYTEDANCE_V1_PRO_FAST_I2V = 'bytedance-v1-pro-fast-i2v',
  BYTEDANCE_V1_PRO_I2V = 'bytedance-v1-pro-i2v',
  HAILUO_02_I2V_PRO = 'hailuo-02-i2v-pro',
  HAILUO_02_I2V_STANDARD = 'hailuo-02-i2v-standard',
  HAILUO_2_3_I2V_PRO = 'hailuo-2.3-i2v-pro',
  HAILUO_2_3_I2V_STANDARD = 'hailuo-2.3-i2v-standard',
  HAPPYHORSE_I2V = 'happyhorse-i2v',
  HAPPYHORSE_1_1_I2V = 'happyhorse-1.1-i2v',
  KLING_V2_1_MASTER_I2V = 'kling-v2.1-master-i2v',
  KLING_V2_5_TURBO_I2V_PRO = 'kling-v2.5-turbo-i2v-pro',
  KLING_V3_TURBO_I2V = 'kling-v3-turbo-i2v',
  WAN_2_2_A14B_I2V_TURBO = 'wan-2.2-a14b-i2v-turbo',
  WAN_2_5_I2V = 'wan-2.5-i2v',
  WAN_2_6_FLASH_I2V = 'wan-2.6-flash-i2v',
  WAN_2_6_I2V = 'wan-2.6-i2v',
  WAN_2_7_I2V = 'wan-2.7-i2v',
}

export const VIDEO_EDITOR_MODELS = [
  VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT,
  VideoGenerationModel.WAN_2_7_VIDEO_EDIT,
  VideoGenerationModel.SEEDANCE_2,
] as const;

export type VideoEditorModel = (typeof VIDEO_EDITOR_MODELS)[number];

export function isVideoEditorModel(
  model: VideoGenerationModel | string
): model is VideoEditorModel {
  return VIDEO_EDITOR_MODELS.includes(model as VideoEditorModel);
}

export enum VideoAspectRatio {
  TWO_TO_THREE = '2:3',
  THREE_TO_TWO = '3:2',
  ONE_TO_ONE = '1:1',
  FOUR_TO_THREE = '4:3',
  THREE_TO_FOUR = '3:4',
  FOUR_TO_FIVE = '4:5',
  FIVE_TO_FOUR = '5:4',
  NINE_TO_SIXTEEN = '9:16',
  SIXTEEN_TO_NINE = '16:9',
  NINE_TO_TWENTY_ONE = '9:21',
  TWENTY_ONE_TO_NINE = '21:9',
  AUTO = 'auto',
  ADAPTIVE = 'adaptive',
}

export enum VideoResolution {
  P480 = '480p',
  P720 = '720p',
  P768 = '768p',
  P1080 = '1080p',
  P2K = '2k',
  P4K = '4k',
}

export enum VideoGenerationMode {
  NORMAL = 'normal',
  FUN = 'fun',
  SPICY = 'spicy',
}

export const TEXT_TO_VIDEO_FREE_TRIAL_PRESET = {
  cost: 1,
  duration: 4,
  model: VideoGenerationModel.SEEDANCE_1_5_PRO,
  resolution: VideoResolution.P480,
} as const;

export const IMAGE_TO_VIDEO_FREE_TRIAL_PRESET = {
  cost: 1,
  duration: 4,
  model: VideoGenerationModel.GROK_IMAGINE_1_5,
  resolution: VideoResolution.P480,
} as const;

export const REFERENCE_TO_VIDEO_DEFAULTS = {
  aspectRatio: VideoAspectRatio.AUTO,
  duration: 8,
  model: VideoGenerationModel.GROK_IMAGINE_1_5,
  resolution: VideoResolution.P480,
} as const;

export type VideoProvider = 'kie' | 'wavespeed';

export type VideoGenerationWorkflowType =
  | 'text-to-video'
  | 'image-to-video'
  | 'reference-to-video'
  | 'video-upscale';

export type VideoGeneratorWorkflow = Exclude<
  VideoGenerationWorkflowType,
  'video-upscale'
>;

export function usesVideoReferenceImages(
  workflow: VideoGenerationWorkflowType | string | undefined
) {
  return workflow === 'image-to-video' || workflow === 'reference-to-video';
}

export type VideoImageInputMode =
  | 'image_urls'
  | 'image_url'
  | 'image_url_with_end'
  | 'input_urls'
  | 'reference_image_urls'
  | 'first_last_frame'
  | 'veo_frames';

type KieDurationFormat = 'number' | 'string';
type KieResolutionFormat = 'lowercase' | 'upper-p';

export interface KieVideoInputConfig {
  imageInput?: VideoImageInputMode;
  includeAspectRatio?: boolean;
  includeDuration?: boolean;
  includeResolution?: boolean;
  durationFormat?: KieDurationFormat;
  resolutionFormat?: KieResolutionFormat;
  extraInput?: Record<string, unknown>;
}

export interface VideoModelWorkflowOptions {
  supportedAspectRatios: VideoAspectRatio[];
  supportedDurations: number[];
  supportedResolutions: VideoResolution[];
  defaultAspectRatio: VideoAspectRatio;
  defaultDuration: number;
  defaultResolution: VideoResolution;
}

export interface VideoImageInputLimits {
  min: number;
  max: number;
}

export interface VideoModelConfig extends VideoModelWorkflowOptions {
  id: VideoGenerationModel;
  displayName: string;
  description: string;
  shortDescription: string;
  promptMaxLength: number;
  provider: VideoProvider;
  providerModelId: string | ((params: VideoGenerationRequest) => string);
  providerImageModelId?: string;
  supportsTextToVideo?: boolean;
  supportedAspectRatios: VideoAspectRatio[];
  supportedDurations: number[];
  supportedResolutions: VideoResolution[];
  defaultAspectRatio: VideoAspectRatio;
  defaultDuration: number;
  defaultResolution: VideoResolution;
  supportsAspectRatioControl?: boolean;
  supportsDurationControl?: boolean;
  supportsResolutionControl?: boolean;
  supportsAudio?: boolean;
  audioCreditsPerSecond?: number;
  supportsImageToVideo?: boolean;
  supportsReferenceToVideo?: boolean;
  supportsEndFrame?: boolean;
  imageInputLimits?: VideoImageInputLimits;
  supportedModes?: VideoGenerationMode[];
  externalImageUnsupportedModes?: VideoGenerationMode[];
  imageToVideoOptions?: Partial<VideoModelWorkflowOptions>;
  referenceToVideoOptions?: Partial<VideoModelWorkflowOptions>;
  kie?: KieVideoInputConfig;
  featured?: boolean;
}

export interface VideoGenerationRequest {
  prompt: string;
  model: VideoGenerationModel;
  aspectRatio: VideoAspectRatio;
  duration: number;
  resolution: VideoResolution;
  mode?: VideoGenerationMode;
  generateAudio?: boolean;
  workflow?: VideoGenerationWorkflowType;
  referenceImageUrls?: string[];
  referenceVideoUrls?: string[];
  referenceAudioUrls?: string[];
  startFrameUrl?: string;
  endFrameUrl?: string;
  sourceVideoUrl?: string;
  referenceImageUrl?: string;
  negativePrompt?: string;
  referenceMode?: 'single' | 'multi';
  audioSetting?: 'auto' | 'origin';
  promptExtend?: boolean;
  preserveDuration?: boolean;
  seed?: number;
  upscaleFactor?: '2' | '4';
  extension?: {
    sourceTaskId: string;
    extendAt: number;
    extendSeconds: 6 | 10;
  };
}

export function isMiniMaxH3ReferenceToVideo(
  request: Pick<
    VideoGenerationRequest,
    'referenceImageUrls' | 'referenceMode' | 'workflow'
  >
) {
  if (request.workflow !== 'image-to-video') return false;
  if (request.referenceMode === 'multi') return true;
  return (request.referenceImageUrls?.length ?? 0) > 2;
}

export function getMiniMaxH3KieModel(request: VideoGenerationRequest) {
  if (request.workflow === 'reference-to-video') {
    return 'minimax-h3/reference-to-video';
  }
  if (request.workflow === 'image-to-video') {
    return isMiniMaxH3ReferenceToVideo(request)
      ? 'minimax-h3/reference-to-video'
      : 'minimax-h3/image-to-video';
  }
  return 'minimax-h3/text-to-video';
}

const GROK_RATIOS = [
  VideoAspectRatio.TWO_TO_THREE,
  VideoAspectRatio.THREE_TO_TWO,
  VideoAspectRatio.ONE_TO_ONE,
  VideoAspectRatio.NINE_TO_SIXTEEN,
  VideoAspectRatio.SIXTEEN_TO_NINE,
];

const SOURCE_VIDEO_RATIOS = [
  VideoAspectRatio.TWO_TO_THREE,
  VideoAspectRatio.THREE_TO_TWO,
  VideoAspectRatio.ONE_TO_ONE,
  VideoAspectRatio.FOUR_TO_THREE,
  VideoAspectRatio.THREE_TO_FOUR,
  VideoAspectRatio.FOUR_TO_FIVE,
  VideoAspectRatio.FIVE_TO_FOUR,
  VideoAspectRatio.NINE_TO_SIXTEEN,
  VideoAspectRatio.SIXTEEN_TO_NINE,
  VideoAspectRatio.NINE_TO_TWENTY_ONE,
  VideoAspectRatio.TWENTY_ONE_TO_NINE,
];

const CORE_VIDEO_RATIOS = [
  VideoAspectRatio.SIXTEEN_TO_NINE,
  VideoAspectRatio.NINE_TO_SIXTEEN,
  VideoAspectRatio.ONE_TO_ONE,
];

const WIDE_VIDEO_RATIOS = [
  VideoAspectRatio.ONE_TO_ONE,
  VideoAspectRatio.FOUR_TO_THREE,
  VideoAspectRatio.THREE_TO_FOUR,
  VideoAspectRatio.SIXTEEN_TO_NINE,
  VideoAspectRatio.NINE_TO_SIXTEEN,
  VideoAspectRatio.TWENTY_ONE_TO_NINE,
];

const HAPPYHORSE_RATIOS = [
  VideoAspectRatio.SIXTEEN_TO_NINE,
  VideoAspectRatio.NINE_TO_SIXTEEN,
  VideoAspectRatio.ONE_TO_ONE,
  VideoAspectRatio.FOUR_TO_THREE,
  VideoAspectRatio.THREE_TO_FOUR,
];

const HAPPYHORSE_1_1_RATIOS = [
  ...HAPPYHORSE_RATIOS,
  VideoAspectRatio.FOUR_TO_FIVE,
  VideoAspectRatio.FIVE_TO_FOUR,
  VideoAspectRatio.NINE_TO_TWENTY_ONE,
  VideoAspectRatio.TWENTY_ONE_TO_NINE,
];

const RUNWAY_RATIOS = [
  VideoAspectRatio.SIXTEEN_TO_NINE,
  VideoAspectRatio.FOUR_TO_THREE,
  VideoAspectRatio.ONE_TO_ONE,
  VideoAspectRatio.THREE_TO_FOUR,
  VideoAspectRatio.NINE_TO_SIXTEEN,
];

const FIXED_WIDE = [VideoAspectRatio.SIXTEEN_TO_NINE];

const ONE_IMAGE: VideoImageInputLimits = { min: 1, max: 1 };
const START_END_IMAGES: VideoImageInputLimits = { min: 1, max: 2 };
const GROK_1_5_REFERENCE_IMAGE_LIMITS: VideoImageInputLimits = {
  min: 1,
  max: 7,
};
export type VideoPricingTier = 'economy' | 'standard' | 'premium';

const ECONOMY_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.GROK_IMAGINE,
  VideoGenerationModel.GROK_IMAGINE_1_5,
  VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT,
  VideoGenerationModel.VEO_3_1_LITE,
  VideoGenerationModel.SEEDANCE_1_5_PRO,
  VideoGenerationModel.HAILUO_02_PRO,
  VideoGenerationModel.HAILUO_02_STANDARD,
  VideoGenerationModel.RUNWAY_VIDEO,
  VideoGenerationModel.BYTEDANCE_V1_PRO,
  VideoGenerationModel.BYTEDANCE_V1_LITE,
]);

const PREMIUM_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.SEEDANCE_2,
  VideoGenerationModel.SEEDANCE_2_FAST,
  VideoGenerationModel.SEEDANCE_2_MINI,
  VideoGenerationModel.VEO_3_1_QUALITY,
]);

const STANDARD_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.TOPAZ_VIDEO_UPSCALE,
  VideoGenerationModel.VEO_3_1,
  VideoGenerationModel.KLING_2_1_MASTER,
  VideoGenerationModel.KLING_2_5_TURBO,
  VideoGenerationModel.KLING_2_6,
  VideoGenerationModel.KLING_3,
  VideoGenerationModel.KLING_3_TURBO,
  VideoGenerationModel.WAN_2_2_A14B,
  VideoGenerationModel.WAN_2_5,
  VideoGenerationModel.WAN_2_6,
  VideoGenerationModel.WAN_2_7,
  VideoGenerationModel.WAN_2_7_VIDEO_EDIT,
  VideoGenerationModel.HAPPYHORSE_1,
  VideoGenerationModel.HAPPYHORSE_1_1,
  VideoGenerationModel.GEMINI_OMNI,
  VideoGenerationModel.MINIMAX_H3,
]);

const PRICED_IMAGE_TO_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.GROK_IMAGINE_1_5,
  VideoGenerationModel.GROK_IMAGINE,
  VideoGenerationModel.VEO_3_1,
  VideoGenerationModel.VEO_3_1_QUALITY,
  VideoGenerationModel.VEO_3_1_LITE,
  VideoGenerationModel.SEEDANCE_2,
  VideoGenerationModel.SEEDANCE_2_FAST,
  VideoGenerationModel.SEEDANCE_1_5_PRO,
  VideoGenerationModel.KLING_3,
  VideoGenerationModel.KLING_2_6,
  VideoGenerationModel.MINIMAX_H3,
]);

const PRICED_TEXT_TO_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.GROK_IMAGINE,
  VideoGenerationModel.VEO_3_1,
  VideoGenerationModel.VEO_3_1_QUALITY,
  VideoGenerationModel.VEO_3_1_LITE,
  VideoGenerationModel.SEEDANCE_2,
  VideoGenerationModel.SEEDANCE_2_FAST,
  VideoGenerationModel.SEEDANCE_1_5_PRO,
  VideoGenerationModel.KLING_3,
  VideoGenerationModel.KLING_2_6,
  VideoGenerationModel.MINIMAX_H3,
]);

const PRICED_REFERENCE_TO_VIDEO_MODELS = new Set<VideoGenerationModel>([
  VideoGenerationModel.GROK_IMAGINE_1_5,
  VideoGenerationModel.MINIMAX_H3,
]);

function rangeInclusive(min: number, max: number) {
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

export const VIDEO_MODEL_CONFIGS: Record<
  VideoGenerationModel,
  VideoModelConfig
> = {
  [VideoGenerationModel.TOPAZ_VIDEO_UPSCALE]: {
    id: VideoGenerationModel.TOPAZ_VIDEO_UPSCALE,
    displayName: 'Topaz Video Upscale',
    description: 'AI video upscaling for uploaded MP4 and MOV files.',
    shortDescription: '2x or 4x video upscale',
    provider: 'kie',
    providerModelId: 'topaz/video-upscale',
    supportsTextToVideo: false,
    supportsImageToVideo: false,
    supportedAspectRatios: [VideoAspectRatio.AUTO],
    supportedDurations: [1],
    supportedResolutions: [VideoResolution.P1080, VideoResolution.P4K],
    defaultAspectRatio: VideoAspectRatio.AUTO,
    defaultDuration: 1,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
  },
  [VideoGenerationModel.GROK_IMAGINE]: {
    id: VideoGenerationModel.GROK_IMAGINE,
    displayName: 'Grok Imagine',
    description: 'KIE Grok Imagine for fast prompt-to-video drafts.',
    shortDescription: 'Lowest-cost Grok drafts',
    provider: 'kie',
    providerModelId: 'grok-imagine/text-to-video',
    providerImageModelId: 'grok-imagine/image-to-video',
    supportedAspectRatios: GROK_RATIOS,
    supportedDurations: rangeInclusive(6, 30),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.TWO_TO_THREE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P480,
    promptMaxLength: 5000,
    supportsImageToVideo: true,
    imageInputLimits: { min: 1, max: 7 },
    supportedModes: [
      VideoGenerationMode.NORMAL,
      VideoGenerationMode.FUN,
      VideoGenerationMode.SPICY,
    ],
    externalImageUnsupportedModes: [VideoGenerationMode.SPICY],
    kie: {
      imageInput: 'image_urls',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: { nsfw_checker: true },
    },
    featured: true,
  },
  [VideoGenerationModel.GROK_IMAGINE_1_5]: {
    id: VideoGenerationModel.GROK_IMAGINE_1_5,
    displayName: 'Grok Imagine 1.5',
    description: 'Grok Imagine Video 1.5 image-to-video generation on KIE.',
    shortDescription: 'Latest Grok i2v',
    provider: 'kie',
    providerModelId: 'grok-imagine-video-1-5-preview',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    supportsReferenceToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: [...GROK_RATIOS, VideoAspectRatio.AUTO],
    supportedDurations: rangeInclusive(1, 15),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.AUTO,
    defaultDuration: 4,
    defaultResolution: VideoResolution.P480,
    referenceToVideoOptions: {
      supportedAspectRatios: [...GROK_RATIOS, VideoAspectRatio.AUTO],
      supportedDurations: rangeInclusive(1, 15),
      supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
      defaultAspectRatio: VideoAspectRatio.AUTO,
      defaultDuration: 8,
      defaultResolution: VideoResolution.P480,
    },
    promptMaxLength: 4096,
    kie: {
      imageInput: 'image_urls',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      resolutionFormat: 'lowercase',
      extraInput: { nsfw_checker: true },
    },
  },
  [VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT]: {
    id: VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT,
    displayName: 'Grok Imagine',
    description: 'Prompt-guided Grok video editing through WaveSpeed.',
    shortDescription: 'Fast Grok video edits',
    provider: 'wavespeed',
    providerModelId: 'x-ai/grok-imagine-video/edit-video',
    supportsTextToVideo: false,
    supportsImageToVideo: false,
    supportedAspectRatios: SOURCE_VIDEO_RATIOS,
    supportedDurations: rangeInclusive(1, 8),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 4,
    defaultResolution: VideoResolution.P480,
    promptMaxLength: 5000,
    supportsAspectRatioControl: false,
  },
  [VideoGenerationModel.VEO_3_1]: {
    id: VideoGenerationModel.VEO_3_1,
    displayName: 'Veo 3.1 Fast',
    description: 'Google Veo 3.1 fast text-to-video generation through KIE.',
    shortDescription: 'Balanced Veo quality',
    provider: 'kie',
    providerModelId: 'veo3_fast',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.NINE_TO_SIXTEEN,
      VideoAspectRatio.AUTO,
    ],
    supportedDurations: [4, 6, 8],
    supportedResolutions: [
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 8,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
  },
  [VideoGenerationModel.VEO_3_1_QUALITY]: {
    id: VideoGenerationModel.VEO_3_1_QUALITY,
    displayName: 'Veo 3.1 Quality',
    description: 'Google Veo 3.1 quality generation through KIE.',
    shortDescription: 'Best Veo detail',
    provider: 'kie',
    providerModelId: 'veo3',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.NINE_TO_SIXTEEN,
      VideoAspectRatio.AUTO,
    ],
    supportedDurations: [4, 6, 8],
    supportedResolutions: [
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 8,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
  },
  [VideoGenerationModel.VEO_3_1_LITE]: {
    id: VideoGenerationModel.VEO_3_1_LITE,
    displayName: 'Veo 3.1 Lite',
    description: 'Google Veo 3.1 lite generation through KIE.',
    shortDescription: 'Cheaper Veo drafts',
    provider: 'kie',
    providerModelId: 'veo3_lite',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.NINE_TO_SIXTEEN,
      VideoAspectRatio.AUTO,
    ],
    supportedDurations: [4, 6, 8],
    supportedResolutions: [
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 8,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
  },
  [VideoGenerationModel.SEEDANCE_2]: {
    id: VideoGenerationModel.SEEDANCE_2,
    displayName: 'Seedance 2.0',
    description: 'Seedance 2.0 high quality multimodal video generation.',
    shortDescription: 'Premium 4K + audio',
    provider: 'kie',
    providerModelId: 'bytedance/seedance-2',
    supportedAspectRatios: [...WIDE_VIDEO_RATIOS, VideoAspectRatio.ADAPTIVE],
    supportedDurations: rangeInclusive(4, 15),
    supportedResolutions: [
      VideoResolution.P480,
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 20000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: { min: 1, max: 9 },
    kie: {
      imageInput: 'reference_image_urls',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      resolutionFormat: 'lowercase',
      extraInput: {
        return_last_frame: false,
        web_search: false,
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.SEEDANCE_2_FAST]: {
    id: VideoGenerationModel.SEEDANCE_2_FAST,
    displayName: 'Seedance 2.0 Fast',
    description: 'Seedance 2.0 fast model for lower-latency video creation.',
    shortDescription: 'Fast Seedance clips',
    provider: 'kie',
    providerModelId: 'bytedance/seedance-2-fast',
    supportedAspectRatios: [...WIDE_VIDEO_RATIOS, VideoAspectRatio.ADAPTIVE],
    supportedDurations: rangeInclusive(4, 15),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 20000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: { min: 1, max: 9 },
    kie: {
      imageInput: 'reference_image_urls',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      resolutionFormat: 'lowercase',
      extraInput: {
        return_last_frame: false,
        web_search: false,
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.SEEDANCE_2_MINI]: {
    id: VideoGenerationModel.SEEDANCE_2_MINI,
    displayName: 'Seedance 2.0 Mini',
    description: 'Seedance 2.0 mini model for efficient KIE video generation.',
    shortDescription: 'Budget Seedance audio',
    provider: 'kie',
    providerModelId: 'bytedance/seedance-2-mini',
    supportedAspectRatios: [...WIDE_VIDEO_RATIOS, VideoAspectRatio.ADAPTIVE],
    supportedDurations: rangeInclusive(4, 15),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 20000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
  },
  [VideoGenerationModel.SEEDANCE_2_5]: {
    id: VideoGenerationModel.SEEDANCE_2_5,
    displayName: 'Seedance 2.5',
    description:
      'Seedance 2.5 reference-video edits. Used by trend templates only.',
    shortDescription: 'Full-length 480p and 720p',
    provider: 'kie',
    providerModelId: 'bytedance/seedance-2-5',
    supportsTextToVideo: false,
    supportsImageToVideo: false,
    supportedAspectRatios: [...WIDE_VIDEO_RATIOS, VideoAspectRatio.ADAPTIVE],
    supportedDurations: rangeInclusive(4, 30),
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 15,
    defaultResolution: VideoResolution.P480,
    promptMaxLength: 20000,
    supportsAudio: false,
  },
  [VideoGenerationModel.SEEDANCE_1_5_PRO]: {
    id: VideoGenerationModel.SEEDANCE_1_5_PRO,
    displayName: 'Seedance 1.5 Pro',
    description: 'Seedance 1.5 Pro cinematic video with optional audio.',
    shortDescription: 'Cinematic audio budget',
    provider: 'kie',
    providerModelId: 'bytedance/seedance-1.5-pro',
    supportedAspectRatios: WIDE_VIDEO_RATIOS,
    supportedDurations: rangeInclusive(4, 12),
    supportedResolutions: [
      VideoResolution.P480,
      VideoResolution.P720,
      VideoResolution.P1080,
    ],
    defaultAspectRatio: VideoAspectRatio.ONE_TO_ONE,
    defaultDuration: 4,
    defaultResolution: VideoResolution.P480,
    promptMaxLength: 20000,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    kie: {
      imageInput: 'input_urls',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      resolutionFormat: 'lowercase',
      extraInput: { fixed_lens: true, nsfw_checker: true },
    },
  },
  [VideoGenerationModel.KLING_3]: {
    id: VideoGenerationModel.KLING_3,
    displayName: 'Kling 3.0',
    description: 'Kling 3.0 video generation with standard, pro, and 4K modes.',
    shortDescription: 'Best Kling 4K',
    provider: 'kie',
    providerModelId: 'kling-3.0/video',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: rangeInclusive(3, 15),
    supportedResolutions: [
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
    supportsAudio: true,
    audioCreditsPerSecond: 10,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    kie: {
      imageInput: 'image_urls',
      includeAspectRatio: true,
      includeDuration: true,
      durationFormat: 'string',
    },
  },
  [VideoGenerationModel.KLING_3_TURBO]: {
    id: VideoGenerationModel.KLING_3_TURBO,
    displayName: 'Kling 3.0 Turbo',
    description: 'Kling 3.0 Turbo text-to-video for faster cinematic output.',
    shortDescription: 'Fast Kling drafts',
    provider: 'kie',
    providerModelId: 'kling/v3-turbo-text-to-video',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: rangeInclusive(3, 15),
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 2500,
  },
  [VideoGenerationModel.KLING_2_6]: {
    id: VideoGenerationModel.KLING_2_6,
    displayName: 'Kling 2.6',
    description: 'Kling 2.6 short prompt videos with optional sound.',
    shortDescription: 'Short Kling with audio',
    provider: 'kie',
    providerModelId: 'kling-2.6/text-to-video',
    providerImageModelId: 'kling-2.6/image-to-video',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: [5, 10],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.ONE_TO_ONE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 1000,
    supportsResolutionControl: false,
    supportsAudio: true,
    audioCreditsPerSecond: 10,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    imageToVideoOptions: {
      supportedAspectRatios: [VideoAspectRatio.AUTO],
      defaultAspectRatio: VideoAspectRatio.AUTO,
    },
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      durationFormat: 'string',
    },
  },
  [VideoGenerationModel.KLING_2_5_TURBO]: {
    id: VideoGenerationModel.KLING_2_5_TURBO,
    displayName: 'Kling 2.5 Turbo',
    description: 'Kling 2.5 Turbo Pro text-to-video generation.',
    shortDescription: 'Simple Kling drafts',
    provider: 'kie',
    providerModelId: 'kling/v2-5-turbo-text-to-video-pro',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: [5, 10],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 2500,
    supportsResolutionControl: false,
  },
  [VideoGenerationModel.KLING_2_1_MASTER]: {
    id: VideoGenerationModel.KLING_2_1_MASTER,
    displayName: 'Kling 2.1 Master',
    description: 'Kling 2.1 Master text-to-video generation.',
    shortDescription: 'Classic Kling style',
    provider: 'kie',
    providerModelId: 'kling/v2-1-master-text-to-video',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: [5, 10],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    supportsResolutionControl: false,
  },
  [VideoGenerationModel.WAN_2_7]: {
    id: VideoGenerationModel.WAN_2_7,
    displayName: 'Wan 2.7',
    description: 'Wan 2.7 text-to-video with ratio and resolution control.',
    shortDescription: 'Flexible Wan video',
    provider: 'kie',
    providerModelId: 'wan/2-7-text-to-video',
    supportedAspectRatios: HAPPYHORSE_RATIOS,
    supportedDurations: rangeInclusive(2, 15),
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
  },
  [VideoGenerationModel.WAN_2_7_VIDEO_EDIT]: {
    id: VideoGenerationModel.WAN_2_7_VIDEO_EDIT,
    displayName: 'Wan 2.7 Video Edit',
    description: 'Prompt-guided editing for an existing video on KIE.',
    shortDescription: 'Edit an existing video',
    provider: 'kie',
    providerModelId: 'wan/2-7-videoedit',
    supportsTextToVideo: false,
    supportsImageToVideo: false,
    supportedAspectRatios: HAPPYHORSE_RATIOS,
    supportedDurations: rangeInclusive(2, 30),
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
  },
  [VideoGenerationModel.WAN_2_6]: {
    id: VideoGenerationModel.WAN_2_6,
    displayName: 'Wan 2.6',
    description: 'Wan 2.6 text-to-video with duration and resolution presets.',
    shortDescription: 'Wide 1080p only',
    provider: 'kie',
    providerModelId: 'wan/2-6-text-to-video',
    supportedAspectRatios: [VideoAspectRatio.SIXTEEN_TO_NINE],
    supportedDurations: [5, 10, 15],
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
    supportsAspectRatioControl: false,
  },
  [VideoGenerationModel.WAN_2_5]: {
    id: VideoGenerationModel.WAN_2_5,
    displayName: 'Wan 2.5',
    description: 'Wan 2.5 preview text-to-video with native audio output.',
    shortDescription: 'Wan audio clips',
    provider: 'kie',
    providerModelId: 'wan/2-5-text-to-video',
    supportedAspectRatios: CORE_VIDEO_RATIOS,
    supportedDurations: [5, 10],
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 800,
    supportsAudio: true,
    audioCreditsPerSecond: 7,
  },
  [VideoGenerationModel.WAN_2_2_A14B]: {
    id: VideoGenerationModel.WAN_2_2_A14B,
    displayName: 'Wan 2.2 A14B',
    description: 'Wan 2.2 A14B Turbo text-to-video generation.',
    shortDescription: 'Cheap turbo clips',
    provider: 'kie',
    providerModelId: 'wan/2-2-a14b-text-to-video-turbo',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.NINE_TO_SIXTEEN,
    ],
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P480, VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    supportsDurationControl: false,
  },
  [VideoGenerationModel.HAPPYHORSE_1_1]: {
    id: VideoGenerationModel.HAPPYHORSE_1_1,
    displayName: 'HappyHorse 1.1',
    description: 'HappyHorse 1.1 text-to-video with broad ratio coverage.',
    shortDescription: 'Broad ratio motion',
    provider: 'kie',
    providerModelId: 'happyhorse-1-1/text-to-video',
    supportedAspectRatios: HAPPYHORSE_1_1_RATIOS,
    supportedDurations: rangeInclusive(3, 15),
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 4999,
  },
  [VideoGenerationModel.HAPPYHORSE_1]: {
    id: VideoGenerationModel.HAPPYHORSE_1,
    displayName: 'HappyHorse 1.0',
    description: 'HappyHorse 1.0 text-to-video with 720p and 1080p output.',
    shortDescription: 'Reliable low-cost motion',
    provider: 'kie',
    providerModelId: 'happyhorse/text-to-video',
    supportedAspectRatios: HAPPYHORSE_RATIOS,
    supportedDurations: rangeInclusive(3, 15),
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
  },
  [VideoGenerationModel.HAILUO_02_PRO]: {
    id: VideoGenerationModel.HAILUO_02_PRO,
    displayName: 'Hailuo 02 Pro',
    description: 'Hailuo 02 Pro prompt-only video generation.',
    shortDescription: 'Fixed 1080p cinema',
    provider: 'kie',
    providerModelId: 'hailuo/02-text-to-video-pro',
    supportedAspectRatios: [VideoAspectRatio.SIXTEEN_TO_NINE],
    supportedDurations: [6],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 1500,
    supportsAspectRatioControl: false,
    supportsDurationControl: false,
    supportsResolutionControl: false,
  },
  [VideoGenerationModel.HAILUO_02_STANDARD]: {
    id: VideoGenerationModel.HAILUO_02_STANDARD,
    displayName: 'Hailuo 02 Standard',
    description: 'Hailuo 02 Standard text-to-video with duration presets.',
    shortDescription: 'Standard Hailuo draft',
    provider: 'kie',
    providerModelId: 'hailuo/02-text-to-video-standard',
    supportedAspectRatios: [VideoAspectRatio.SIXTEEN_TO_NINE],
    supportedDurations: [6, 10],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 1500,
    supportsAspectRatioControl: false,
    supportsResolutionControl: false,
  },
  [VideoGenerationModel.MINIMAX_H3]: {
    id: VideoGenerationModel.MINIMAX_H3,
    displayName: 'MiniMax H3',
    description:
      'MiniMax H3 (Hailuo 03) multimodal video with 2K output and native audio.',
    shortDescription: '2K Hailuo with audio',
    provider: 'kie',
    providerModelId: getMiniMaxH3KieModel,
    supportedAspectRatios: WIDE_VIDEO_RATIOS,
    supportedDurations: rangeInclusive(4, 15),
    supportedResolutions: [VideoResolution.P768, VideoResolution.P2K],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P768,
    promptMaxLength: 7000,
    supportsImageToVideo: true,
    supportsReferenceToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: { min: 1, max: 9 },
    imageToVideoOptions: {
      supportedAspectRatios: [VideoAspectRatio.ADAPTIVE, ...WIDE_VIDEO_RATIOS],
      defaultAspectRatio: VideoAspectRatio.ADAPTIVE,
    },
    referenceToVideoOptions: {
      supportedAspectRatios: [VideoAspectRatio.ADAPTIVE, ...WIDE_VIDEO_RATIOS],
      defaultAspectRatio: VideoAspectRatio.ADAPTIVE,
      defaultDuration: 6,
    },
    kie: {
      imageInput: 'first_last_frame',
      includeAspectRatio: true,
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'number',
      resolutionFormat: 'upper-p',
    },
    featured: true,
  },
  [VideoGenerationModel.GEMINI_OMNI]: {
    id: VideoGenerationModel.GEMINI_OMNI,
    displayName: 'Gemini Omni',
    description: 'Gemini Omni multimodal video generation through KIE.',
    shortDescription: 'Google 4K video',
    provider: 'kie',
    providerModelId: 'gemini-omni-video',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.NINE_TO_SIXTEEN,
    ],
    supportedDurations: [4, 6, 8, 10],
    supportedResolutions: [
      VideoResolution.P720,
      VideoResolution.P1080,
      VideoResolution.P4K,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 8,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 20000,
  },
  [VideoGenerationModel.RUNWAY_VIDEO]: {
    id: VideoGenerationModel.RUNWAY_VIDEO,
    displayName: 'Runway Video',
    description: 'Runway text-to-video generation through KIE.',
    shortDescription: 'Social-ready Runway',
    provider: 'kie',
    providerModelId: 'runway/generate',
    supportedAspectRatios: RUNWAY_RATIOS,
    supportedDurations: [5, 10],
    supportedResolutions: [VideoResolution.P720, VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 2048,
  },
  [VideoGenerationModel.BYTEDANCE_V1_PRO]: {
    id: VideoGenerationModel.BYTEDANCE_V1_PRO,
    displayName: 'Seedance V1 Pro',
    description: 'Bytedance V1 Pro text-to-video generation on KIE.',
    shortDescription: 'Budget Seedance Pro',
    provider: 'kie',
    providerModelId: 'bytedance/v1-pro-text-to-video',
    supportedAspectRatios: [
      VideoAspectRatio.TWENTY_ONE_TO_NINE,
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.FOUR_TO_THREE,
      VideoAspectRatio.ONE_TO_ONE,
      VideoAspectRatio.THREE_TO_FOUR,
      VideoAspectRatio.NINE_TO_SIXTEEN,
    ],
    supportedDurations: [5, 10],
    supportedResolutions: [
      VideoResolution.P480,
      VideoResolution.P720,
      VideoResolution.P1080,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 10000,
  },
  [VideoGenerationModel.BYTEDANCE_V1_LITE]: {
    id: VideoGenerationModel.BYTEDANCE_V1_LITE,
    displayName: 'Seedance V1 Lite',
    description: 'Bytedance V1 Lite text-to-video generation on KIE.',
    shortDescription: 'Cheapest V1 drafts',
    provider: 'kie',
    providerModelId: 'bytedance/v1-lite-text-to-video',
    supportedAspectRatios: [
      VideoAspectRatio.SIXTEEN_TO_NINE,
      VideoAspectRatio.FOUR_TO_THREE,
      VideoAspectRatio.ONE_TO_ONE,
      VideoAspectRatio.THREE_TO_FOUR,
      VideoAspectRatio.NINE_TO_SIXTEEN,
      VideoAspectRatio.NINE_TO_TWENTY_ONE,
    ],
    supportedDurations: [5, 10],
    supportedResolutions: [
      VideoResolution.P480,
      VideoResolution.P720,
      VideoResolution.P1080,
    ],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 10000,
  },
  [VideoGenerationModel.BYTEDANCE_V1_LITE_I2V]: {
    id: VideoGenerationModel.BYTEDANCE_V1_LITE_I2V,
    displayName: 'Bytedance V1 Lite',
    description:
      'KIE Bytedance V1 Lite image-to-video with optional end frame.',
    shortDescription: 'Two-frame budget motion',
    provider: 'kie',
    providerModelId: 'bytedance/v1-lite-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 10000,
    kie: {
      imageInput: 'image_url_with_end',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: {
        camera_fixed: false,
        seed: -1,
        enable_safety_checker: true,
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.BYTEDANCE_V1_PRO_FAST_I2V]: {
    id: VideoGenerationModel.BYTEDANCE_V1_PRO_FAST_I2V,
    displayName: 'Bytedance V1 Pro Fast',
    description: 'KIE Bytedance fast image-to-video generation.',
    shortDescription: 'Fast single-frame motion',
    provider: 'kie',
    providerModelId: 'bytedance/v1-pro-fast-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 10000,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: { nsfw_checker: true },
    },
  },
  [VideoGenerationModel.BYTEDANCE_V1_PRO_I2V]: {
    id: VideoGenerationModel.BYTEDANCE_V1_PRO_I2V,
    displayName: 'Bytedance V1 Pro',
    description: 'KIE Bytedance Pro image-to-video generation.',
    shortDescription: 'Better ByteDance motion',
    provider: 'kie',
    providerModelId: 'bytedance/v1-pro-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 10000,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: {
        camera_fixed: false,
        seed: -1,
        enable_safety_checker: true,
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.HAILUO_02_I2V_PRO]: {
    id: VideoGenerationModel.HAILUO_02_I2V_PRO,
    displayName: 'Hailuo 02 Pro',
    description: 'KIE Hailuo 02 Pro image-to-video with optional end image.',
    shortDescription: 'Pro two-frame transitions',
    provider: 'kie',
    providerModelId: 'hailuo/02-image-to-video-pro',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [6],
    supportedResolutions: [VideoResolution.P768],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P768,
    promptMaxLength: 1500,
    kie: {
      imageInput: 'image_url_with_end',
      extraInput: { prompt_optimizer: true },
    },
  },
  [VideoGenerationModel.HAILUO_02_I2V_STANDARD]: {
    id: VideoGenerationModel.HAILUO_02_I2V_STANDARD,
    displayName: 'Hailuo 02 Standard',
    description: 'KIE Hailuo 02 Standard image-to-video with fixed output.',
    shortDescription: 'Longer fixed Hailuo',
    provider: 'kie',
    providerModelId: 'hailuo/02-image-to-video-standard',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [10],
    supportedResolutions: [VideoResolution.P768],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 10,
    defaultResolution: VideoResolution.P768,
    promptMaxLength: 1500,
    kie: {
      imageInput: 'image_url_with_end',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'upper-p',
      extraInput: { prompt_optimizer: true },
    },
  },
  [VideoGenerationModel.HAILUO_2_3_I2V_PRO]: {
    id: VideoGenerationModel.HAILUO_2_3_I2V_PRO,
    displayName: 'Hailuo 2.3 Pro',
    description: 'KIE Hailuo 2.3 Pro image-to-video.',
    shortDescription: 'Single-frame Hailuo Pro',
    provider: 'kie',
    providerModelId: 'hailuo/2-3-image-to-video-pro',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [6],
    supportedResolutions: [VideoResolution.P768],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P768,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'upper-p',
    },
  },
  [VideoGenerationModel.HAILUO_2_3_I2V_STANDARD]: {
    id: VideoGenerationModel.HAILUO_2_3_I2V_STANDARD,
    displayName: 'Hailuo 2.3 Standard',
    description: 'KIE Hailuo 2.3 Standard image-to-video.',
    shortDescription: 'Budget Hailuo 2.3',
    provider: 'kie',
    providerModelId: 'hailuo/2-3-image-to-video-standard',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [6],
    supportedResolutions: [VideoResolution.P768],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 6,
    defaultResolution: VideoResolution.P768,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'upper-p',
    },
  },
  [VideoGenerationModel.HAPPYHORSE_I2V]: {
    id: VideoGenerationModel.HAPPYHORSE_I2V,
    displayName: 'HappyHorse 1.0',
    description: 'KIE HappyHorse image-to-video at 1080p.',
    shortDescription: 'Cheap 1080p motion',
    provider: 'kie',
    providerModelId: 'happyhorse/image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 4999,
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'number',
      resolutionFormat: 'lowercase',
      extraInput: { seed: 1546095068 },
    },
  },
  [VideoGenerationModel.HAPPYHORSE_1_1_I2V]: {
    id: VideoGenerationModel.HAPPYHORSE_1_1_I2V,
    displayName: 'HappyHorse 1.1',
    description: 'KIE HappyHorse 1.1 image-to-video at 1080p.',
    shortDescription: 'Updated 1080p motion',
    provider: 'kie',
    providerModelId: 'happyhorse-1-1/image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 4999,
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'number',
      resolutionFormat: 'lowercase',
    },
  },
  [VideoGenerationModel.KLING_V2_1_MASTER_I2V]: {
    id: VideoGenerationModel.KLING_V2_1_MASTER_I2V,
    displayName: 'Kling V2.1 Master',
    description: 'KIE Kling V2.1 Master image-to-video.',
    shortDescription: 'Classic Kling i2v',
    provider: 'kie',
    providerModelId: 'kling/v2-1-master-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      durationFormat: 'string',
      extraInput: {
        negative_prompt: 'blur, distort, and low quality',
        cfg_scale: 0.5,
      },
    },
  },
  [VideoGenerationModel.KLING_V2_5_TURBO_I2V_PRO]: {
    id: VideoGenerationModel.KLING_V2_5_TURBO_I2V_PRO,
    displayName: 'Kling V2.5 Turbo Pro',
    description: 'KIE Kling V2.5 Turbo Pro image-to-video.',
    shortDescription: 'Turbo Kling i2v',
    provider: 'kie',
    providerModelId: 'kling/v2-5-turbo-image-to-video-pro',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 2500,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      durationFormat: 'string',
      extraInput: {
        negative_prompt: 'blur, distort, and low quality',
        cfg_scale: 0.5,
      },
    },
  },
  [VideoGenerationModel.KLING_V3_TURBO_I2V]: {
    id: VideoGenerationModel.KLING_V3_TURBO_I2V,
    displayName: 'Kling V3 Turbo',
    description: 'KIE Kling V3 Turbo image-to-video at 720p.',
    shortDescription: 'Fast Kling V3 i2v',
    provider: 'kie',
    providerModelId: 'kling/v3-turbo-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 2500,
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
    },
  },
  [VideoGenerationModel.WAN_2_2_A14B_I2V_TURBO]: {
    id: VideoGenerationModel.WAN_2_2_A14B_I2V_TURBO,
    displayName: 'Wan 2.2 A14B Turbo',
    description: 'KIE Wan 2.2 A14B image-to-video turbo.',
    shortDescription: 'Cheap Wan turbo',
    provider: 'kie',
    providerModelId: 'wan/2-2-a14b-image-to-video-turbo',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P720],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P720,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'image_url',
      includeResolution: true,
      resolutionFormat: 'lowercase',
      extraInput: {
        enable_prompt_expansion: false,
        seed: 0,
        acceleration: 'none',
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.WAN_2_5_I2V]: {
    id: VideoGenerationModel.WAN_2_5_I2V,
    displayName: 'Wan 2.5',
    description: 'KIE Wan 2.5 image-to-video with 1080p output.',
    shortDescription: '1080p lip-sync ready',
    provider: 'kie',
    providerModelId: 'wan/2-5-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 800,
    kie: {
      imageInput: 'image_url',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: {
        negative_prompt: 'blurry, flicker, camera shake, low quality',
        enable_prompt_expansion: true,
        seed: 123456,
        nsfw_checker: true,
      },
    },
  },
  [VideoGenerationModel.WAN_2_6_FLASH_I2V]: {
    id: VideoGenerationModel.WAN_2_6_FLASH_I2V,
    displayName: 'Wan 2.6 Flash',
    description: 'KIE Wan 2.6 Flash image-to-video at 1080p.',
    shortDescription: 'Fast Wan 1080p',
    provider: 'kie',
    providerModelId: 'wan/2-6-flash-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 1500,
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: { audio: false, multi_shots: false, nsfw_checker: true },
    },
  },
  [VideoGenerationModel.WAN_2_6_I2V]: {
    id: VideoGenerationModel.WAN_2_6_I2V,
    displayName: 'Wan 2.6',
    description: 'KIE Wan 2.6 image-to-video at 1080p.',
    shortDescription: 'Stable Wan 1080p',
    provider: 'kie',
    providerModelId: 'wan/2-6-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    imageInputLimits: ONE_IMAGE,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'image_urls',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'string',
      resolutionFormat: 'lowercase',
      extraInput: { nsfw_checker: true },
    },
  },
  [VideoGenerationModel.WAN_2_7_I2V]: {
    id: VideoGenerationModel.WAN_2_7_I2V,
    displayName: 'Wan 2.7',
    description: 'KIE Wan 2.7 image-to-video with first or last frame.',
    shortDescription: 'Two-frame Wan 1080p',
    provider: 'kie',
    providerModelId: 'wan/2-7-image-to-video',
    supportsTextToVideo: false,
    supportsImageToVideo: true,
    supportsEndFrame: true,
    imageInputLimits: START_END_IMAGES,
    supportedAspectRatios: FIXED_WIDE,
    supportedDurations: [5],
    supportedResolutions: [VideoResolution.P1080],
    defaultAspectRatio: VideoAspectRatio.SIXTEEN_TO_NINE,
    defaultDuration: 5,
    defaultResolution: VideoResolution.P1080,
    promptMaxLength: 5000,
    kie: {
      imageInput: 'first_last_frame',
      includeDuration: true,
      includeResolution: true,
      durationFormat: 'number',
      resolutionFormat: 'lowercase',
      extraInput: { prompt_extend: true, watermark: false, seed: 123456 },
    },
  },
};

export const ALL_VIDEO_MODELS = Object.values(VIDEO_MODEL_CONFIGS);

export function getVideoModelConfig(model: VideoGenerationModel | string) {
  return VIDEO_MODEL_CONFIGS[model as VideoGenerationModel];
}

export function getVideoImageInputLimits(
  config: Pick<VideoModelConfig, 'id' | 'imageInputLimits'> | undefined,
  workflow: VideoGenerationWorkflowType = 'text-to-video'
): VideoImageInputLimits {
  if (
    workflow === 'reference-to-video' &&
    config?.id === VideoGenerationModel.GROK_IMAGINE_1_5
  ) {
    return GROK_1_5_REFERENCE_IMAGE_LIMITS;
  }
  return config?.imageInputLimits ?? ONE_IMAGE;
}

export function getVideoImageInputMax(
  config: Pick<VideoModelConfig, 'id' | 'imageInputLimits'> | undefined,
  workflow: VideoGenerationWorkflowType = 'text-to-video'
) {
  return getVideoImageInputLimits(config, workflow).max;
}

export function canUseVideoEndFrame(
  config:
    | Pick<VideoModelConfig, 'imageInputLimits' | 'supportsEndFrame'>
    | undefined
) {
  return Boolean(config?.supportsEndFrame && getVideoImageInputMax(config) > 1);
}

export function getVideoModelsForWorkflow(
  workflow: VideoGenerationWorkflowType
) {
  if (workflow === 'video-upscale') {
    return [VIDEO_MODEL_CONFIGS[VideoGenerationModel.TOPAZ_VIDEO_UPSCALE]];
  }
  if (workflow === 'reference-to-video') {
    return ALL_VIDEO_MODELS.filter(
      (config) =>
        Boolean(config.supportsReferenceToVideo) &&
        isVideoModelPricedForWorkflow(config.id, workflow)
    );
  }

  const models = ALL_VIDEO_MODELS.filter((config) =>
    workflow === 'image-to-video'
      ? Boolean(config.supportsImageToVideo) &&
        isVideoModelPricedForWorkflow(config.id, workflow)
      : config.supportsTextToVideo !== false &&
        isVideoModelPricedForWorkflow(config.id, workflow)
  );

  if (workflow !== 'image-to-video') {
    return models;
  }

  return [...models].sort(
    (a, b) =>
      getImageToVideoModelPriority(a.id) - getImageToVideoModelPriority(b.id)
  );
}

function getImageToVideoModelPriority(model: VideoGenerationModel) {
  switch (model) {
    case VideoGenerationModel.GROK_IMAGINE_1_5:
      return 0;
    case VideoGenerationModel.GROK_IMAGINE:
      return 1;
    default:
      return 10;
  }
}

export function getVideoModelWorkflowOptions(
  model: VideoGenerationModel | string | VideoModelConfig,
  workflow: VideoGenerationWorkflowType = 'text-to-video'
): VideoModelWorkflowOptions {
  const config = typeof model === 'string' ? getVideoModelConfig(model) : model;
  if (!config) {
    return VIDEO_MODEL_CONFIGS[VideoGenerationModel.GROK_IMAGINE];
  }

  const workflowOptions =
    workflow === 'image-to-video'
      ? config.imageToVideoOptions
      : workflow === 'reference-to-video'
        ? config.referenceToVideoOptions
        : undefined;

  return {
    supportedAspectRatios:
      workflowOptions?.supportedAspectRatios ?? config.supportedAspectRatios,
    supportedDurations:
      workflowOptions?.supportedDurations ?? config.supportedDurations,
    supportedResolutions:
      workflowOptions?.supportedResolutions ?? config.supportedResolutions,
    defaultAspectRatio:
      workflowOptions?.defaultAspectRatio ?? config.defaultAspectRatio,
    defaultDuration: workflowOptions?.defaultDuration ?? config.defaultDuration,
    defaultResolution:
      workflowOptions?.defaultResolution ?? config.defaultResolution,
  };
}

export function getVideoDurationRange(durations: number[]) {
  if (durations.length === 0) {
    return { min: 1, max: 1 };
  }

  return {
    min: Math.min(...durations),
    max: Math.max(...durations),
  };
}

export function isContinuousVideoDurationRange(durations: number[]) {
  return durations.every(
    (duration, index) => index === 0 || duration === durations[index - 1] + 1
  );
}

export function getVideoPricingTier({
  model,
  resolution,
}: {
  model: VideoGenerationModel | string;
  resolution: VideoResolution | string;
}): VideoPricingTier {
  if (
    PREMIUM_VIDEO_MODELS.has(model as VideoGenerationModel) ||
    (model === VideoGenerationModel.KLING_3 &&
      resolution === VideoResolution.P4K)
  ) {
    return 'premium';
  }

  if (STANDARD_VIDEO_MODELS.has(model as VideoGenerationModel)) {
    return 'standard';
  }

  if (ECONOMY_VIDEO_MODELS.has(model as VideoGenerationModel)) {
    return 'economy';
  }

  throw new Error(`No video pricing tier configured for model: ${model}`);
}

export function calculateVideoGenerationCost({
  model,
  duration,
  resolution,
  generateAudio,
  workflow,
  sourceDuration,
  imageCount,
}: {
  model: VideoGenerationModel | string;
  duration: number;
  resolution: VideoResolution | string;
  generateAudio?: boolean;
  workflow?: VideoGenerationWorkflowType;
  sourceDuration?: number;
  imageCount?: number;
}): number {
  if (model === VideoGenerationModel.SEEDANCE_2_5) {
    return seedance25GenerationCredits({ resolution, seconds: duration });
  }

  const config = getVideoModelConfig(model);
  const hasAudio = Boolean(
    generateAudio && config?.supportsAudio && config.audioCreditsPerSecond
  );
  const creditsFromKie = (kieCredits: number) =>
    creditsFromKieCredits(
      kieCredits +
        (hasAudio ? (config?.audioCreditsPerSecond ?? 0) * duration : 0)
    );

  switch (model) {
    case VideoGenerationModel.TOPAZ_VIDEO_UPSCALE:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P4K ? 14 : 8)
      );
    case VideoGenerationModel.GROK_IMAGINE:
    case VideoGenerationModel.GROK_IMAGINE_1_5:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P720 ? 3 : 1.6)
      );
    case VideoGenerationModel.GROK_IMAGINE_VIDEO_EDIT:
      return creditsFromKieCredits(
        duration *
          (CREDIT_ECONOMICS.grokVideoEditLegacyFrontendCreditsPerSecond /
            CREDIT_ECONOMICS.legacyEconomyFrontendCreditsPerKieCredit)
      );
    case VideoGenerationModel.KLING_2_6:
      return creditsFromKie(duration * 11);
    case VideoGenerationModel.KLING_2_5_TURBO:
      return creditsFromKie(duration * 8.4);
    case VideoGenerationModel.KLING_2_1_MASTER:
      return creditsFromKie(duration * 32);
    case VideoGenerationModel.KLING_3:
      return creditsFromKie(
        duration *
          (resolution === VideoResolution.P4K
            ? 67
            : resolution === VideoResolution.P1080
              ? 18
              : 14)
      );
    case VideoGenerationModel.KLING_3_TURBO:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 22.5 : 18)
      );
    case VideoGenerationModel.SEEDANCE_1_5_PRO:
      return creditsFromKie(
        duration *
          (resolution === VideoResolution.P1080
            ? 7.5
            : resolution === VideoResolution.P720
              ? 3.5
              : 1.75)
      );
    case VideoGenerationModel.SEEDANCE_2:
    case VideoGenerationModel.SEEDANCE_2_FAST:
    case VideoGenerationModel.SEEDANCE_2_MINI: {
      const hasVideoInput = typeof sourceDuration === 'number';
      const billableDuration = duration + (sourceDuration ?? 0);
      return creditsFromKie(
        billableDuration *
          getSeedanceKieCreditsPerSecond({
            hasVideoInput,
            model,
            resolution,
          })
      );
    }
    case VideoGenerationModel.VEO_3_1_LITE:
      return creditsFromKie(30);
    case VideoGenerationModel.VEO_3_1:
      return creditsFromKie(60);
    case VideoGenerationModel.VEO_3_1_QUALITY:
      return creditsFromKie(250);
    case VideoGenerationModel.WAN_2_7:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 24 : 16)
      );
    case VideoGenerationModel.WAN_2_6:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 21 : 14)
      );
    case VideoGenerationModel.WAN_2_5:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 20 : 12)
      );
    case VideoGenerationModel.WAN_2_7_VIDEO_EDIT:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 24 : 16)
      );
    case VideoGenerationModel.WAN_2_2_A14B:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P720 ? 6 : 4)
      );
    case VideoGenerationModel.HAPPYHORSE_1_1:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 29 : 22.5)
      );
    case VideoGenerationModel.HAPPYHORSE_1:
      return creditsFromKie(
        duration * (resolution === VideoResolution.P1080 ? 48 : 28)
      );
    case VideoGenerationModel.HAILUO_02_PRO:
      return creditsFromKie(57);
    case VideoGenerationModel.HAILUO_02_STANDARD:
      return creditsFromKie(duration * 5);
    case VideoGenerationModel.MINIMAX_H3: {
      const extraImageCount =
        workflow === 'reference-to-video'
          ? Math.max(0, (imageCount ?? 0) - REFERENCE_TO_VIDEO_FREE_IMAGE_COUNT)
          : 0;
      const billableDuration =
        workflow === 'reference-to-video'
          ? duration + (sourceDuration ?? 0)
          : duration;
      return creditsFromKie(
        billableDuration * (resolution === VideoResolution.P2K ? 13 : 8) +
          extraImageCount * REFERENCE_TO_VIDEO_EXTRA_IMAGE_KIE_CREDITS
      );
    }
    case VideoGenerationModel.GEMINI_OMNI:
      return creditsFromKie(
        (resolution === VideoResolution.P4K ? 105 : 21) + duration * 10.5
      );
    case VideoGenerationModel.RUNWAY_VIDEO:
      return creditsFromKie(
        resolution === VideoResolution.P1080 || duration === 10 ? 30 : 12
      );
    case VideoGenerationModel.BYTEDANCE_V1_PRO:
      return creditsFromKie(
        duration *
          (resolution === VideoResolution.P1080
            ? 12
            : resolution === VideoResolution.P720
              ? 7
              : 4)
      );
    case VideoGenerationModel.BYTEDANCE_V1_LITE:
      return creditsFromKie(
        duration *
          (resolution === VideoResolution.P1080
            ? 8
            : resolution === VideoResolution.P720
              ? 5
              : 3)
      );
    default:
      throw new Error(`No video credit pricing configured for model: ${model}`);
  }
}

// KIE bills Seedance video inputs over input plus output duration.
function getSeedanceKieCreditsPerSecond({
  hasVideoInput,
  model,
  resolution,
}: {
  hasVideoInput: boolean;
  model: VideoGenerationModel;
  resolution: VideoResolution | string;
}) {
  if (model === VideoGenerationModel.SEEDANCE_2) {
    if (hasVideoInput) {
      if (resolution === VideoResolution.P4K) return 128;
      if (resolution === VideoResolution.P1080) return 62;
      if (resolution === VideoResolution.P720) return 25;
      return 11.5;
    }
    if (resolution === VideoResolution.P4K) return 208;
    if (resolution === VideoResolution.P1080) return 102;
    if (resolution === VideoResolution.P720) return 41;
    return 19;
  }

  if (model === VideoGenerationModel.SEEDANCE_2_FAST) {
    if (hasVideoInput) {
      return resolution === VideoResolution.P720 ? 20 : 9;
    }
    return resolution === VideoResolution.P720 ? 33 : 15.5;
  }

  if (hasVideoInput) {
    return resolution === VideoResolution.P720 ? 12.5 : 6;
  }
  return resolution === VideoResolution.P720 ? 20.5 : 9.5;
}

export function isVideoModelPricedForWorkflow(
  model: VideoGenerationModel | string,
  workflow: VideoGenerationWorkflowType
) {
  if (workflow === 'video-upscale') {
    return model === VideoGenerationModel.TOPAZ_VIDEO_UPSCALE;
  }
  if (workflow === 'image-to-video') {
    return PRICED_IMAGE_TO_VIDEO_MODELS.has(model as VideoGenerationModel);
  }
  if (workflow === 'reference-to-video') {
    return PRICED_REFERENCE_TO_VIDEO_MODELS.has(model as VideoGenerationModel);
  }

  return PRICED_TEXT_TO_VIDEO_MODELS.has(model as VideoGenerationModel);
}

export function getMinimumVideoGenerationCost({
  model,
  workflow = 'text-to-video',
}: {
  model: VideoGenerationModel | string;
  workflow?: VideoGenerationWorkflowType;
}): number {
  const config = getVideoModelConfig(model);
  if (!config) {
    return calculateVideoGenerationCost({
      model,
      duration: 1,
      resolution: VideoResolution.P480,
      workflow,
    });
  }

  const options = getVideoModelWorkflowOptions(config, workflow);
  if (!isVideoModelPricedForWorkflow(config.id, workflow)) {
    throw new Error(`${config.displayName} is not available for ${workflow}.`);
  }
  const costs = options.supportedDurations.flatMap((duration) =>
    options.supportedResolutions.map((resolution) =>
      calculateVideoGenerationCost({
        model: config.id,
        duration,
        resolution,
        generateAudio: false,
        workflow,
      })
    )
  );

  return Math.min(...costs);
}

export function assertVideoRequestSupported(
  request: Omit<VideoGenerationRequest, 'prompt'>
): void {
  const config = getVideoModelConfig(request.model);
  if (!config) {
    throw new Error(`Unknown video model: ${request.model}`);
  }
  const workflow = request.workflow ?? 'text-to-video';
  const options = getVideoModelWorkflowOptions(config, workflow);
  if (!isVideoModelPricedForWorkflow(config.id, workflow)) {
    throw new Error(`${config.displayName} is not available for ${workflow}.`);
  }
  if (!options.supportedAspectRatios.includes(request.aspectRatio)) {
    throw new Error(
      `${config.displayName} does not support aspect ratio "${request.aspectRatio}".`
    );
  }
  const durationRange = getVideoDurationRange(options.supportedDurations);
  if (
    request.duration < durationRange.min ||
    request.duration > durationRange.max ||
    !options.supportedDurations.includes(request.duration)
  ) {
    const isContinuousDuration = isContinuousVideoDurationRange(
      options.supportedDurations
    );
    throw new Error(
      isContinuousDuration
        ? `${config.displayName} supports duration from ${durationRange.min}s to ${durationRange.max}s.`
        : `${config.displayName} supports duration ${options.supportedDurations.join(', ')}s.`
    );
  }
  if (!options.supportedResolutions.includes(request.resolution)) {
    throw new Error(
      `${config.displayName} does not support resolution "${request.resolution}".`
    );
  }
  const combinationError = getVideoOptionCombinationError(request);
  if (combinationError) {
    throw new Error(combinationError);
  }
  if (request.workflow === 'image-to-video') {
    if (!config.supportsImageToVideo) {
      throw new Error(`${config.displayName} does not support image-to-video.`);
    }
    const referenceImageUrls =
      request.referenceImageUrls ??
      [request.startFrameUrl, request.endFrameUrl].filter(
        (url): url is string => Boolean(url)
      );
    const imageInputLimits = getVideoImageInputLimits(config, request.workflow);
    if (referenceImageUrls.length < imageInputLimits.min) {
      throw new Error(
        `${config.displayName} requires at least ${imageInputLimits.min} image.`
      );
    }
    if (referenceImageUrls.length > imageInputLimits.max) {
      throw new Error(
        `${config.displayName} supports up to ${imageInputLimits.max} images.`
      );
    }
    if (request.endFrameUrl && !canUseVideoEndFrame(config)) {
      throw new Error(`${config.displayName} does not support an end frame.`);
    }
    if (
      request.mode &&
      config.externalImageUnsupportedModes?.includes(request.mode)
    ) {
      throw new Error(
        'Spicy mode requires a Crazy Frog AI-generated image and is unavailable for uploaded images.'
      );
    }
  }
  if (request.workflow === 'reference-to-video') {
    if (!config.supportsReferenceToVideo) {
      throw new Error(
        `${config.displayName} does not support reference-to-video.`
      );
    }
    const imageCount = request.referenceImageUrls?.length ?? 0;
    const videoCount = request.referenceVideoUrls?.length ?? 0;
    const audioCount = request.referenceAudioUrls?.length ?? 0;
    if (config.id === VideoGenerationModel.GROK_IMAGINE_1_5) {
      const imageInputLimits = getVideoImageInputLimits(config, workflow);
      if (imageCount < imageInputLimits.min) {
        throw new Error(
          `${config.displayName} requires at least ${imageInputLimits.min} image.`
        );
      }
      if (imageCount > imageInputLimits.max) {
        throw new Error(
          `${config.displayName} supports up to ${imageInputLimits.max} images.`
        );
      }
      if (videoCount > 0 || audioCount > 0) {
        throw new Error(
          `${config.displayName} reference-to-video accepts still images only.`
        );
      }
      return;
    }
    if (imageCount > REFERENCE_TO_VIDEO_IMAGE_MAX) {
      throw new Error(
        `${config.displayName} supports up to ${REFERENCE_TO_VIDEO_IMAGE_MAX} images.`
      );
    }
    if (videoCount > REFERENCE_TO_VIDEO_VIDEO_MAX) {
      throw new Error(
        `${config.displayName} supports up to ${REFERENCE_TO_VIDEO_VIDEO_MAX} videos.`
      );
    }
    if (audioCount > REFERENCE_TO_VIDEO_AUDIO_MAX) {
      throw new Error(
        `${config.displayName} supports up to ${REFERENCE_TO_VIDEO_AUDIO_MAX} audio files.`
      );
    }
    if (imageCount + videoCount + audioCount > REFERENCE_TO_VIDEO_MAX_FILES) {
      throw new Error(
        `${config.displayName} supports up to ${REFERENCE_TO_VIDEO_MAX_FILES} files in total.`
      );
    }
    if (imageCount + videoCount < 1) {
      throw new Error(
        `${config.displayName} requires at least one reference image or video.`
      );
    }
  }
}

export function getVideoOptionCombinationError(
  request: Pick<VideoGenerationRequest, 'model' | 'duration' | 'resolution'>
) {
  if (
    request.model === VideoGenerationModel.RUNWAY_VIDEO &&
    request.duration === 10 &&
    request.resolution === VideoResolution.P1080
  ) {
    return 'Runway Video does not support 10s output at 1080p.';
  }
  return undefined;
}
